import { z } from 'zod';

/**
 * Validated, browser-safe env for US-Shop.
 *
 * Only NON-SECRET, build-time-inlined `VITE_*` values live here — the public
 * storefront holds no service token and no tenant id (the BFF owns both). The
 * one flag that matters is the AC2 EFL publish gate, which is OFF unless the
 * env explicitly sets the string 'true'.
 */
const RawEnvSchema = z.object({
  VITE_FEATURE_EFL_PUBLISH: z.string().optional(),
  VITE_FEATURE_SKIP_EMAIL_VERIFICATION: z.string().optional(),
  VITE_DEV_FUNNEL_RESUME: z.string().optional(),
  VITE_DEV_CAPTCHA_STUB: z.string().optional(),
  VITE_DEV_DEPOSIT_STUB: z.string().optional(),
  VITE_DEPOSIT_AMOUNT_VERIFIED: z.string().optional(),
  VITE_USE_MOCK_ESIID: z.string().optional(),
  VITE_PUCT_REP_LICENSE: z.string().optional(),
});

export interface ShopEnv {
  /**
   * AC2 — when false (the default), the EFL is rendered as an explicitly
   * labelled PREVIEW, never as a live/publishable PUCT §25.475 document. Only a
   * literal 'true' flips it; anything else (unset, '', 'false', '1') is false.
   */
  readonly featureEflPublish: boolean;
  /**
   * DEV-ONLY BYPASS of the email-OTP gate on the credit step (E-SF05). The
   * BFF carries the same flag server-side; both default OFF. ⚠️ Turn BOTH
   * off again before any deploy — the gate is what proves the FCRA
   * adverse-action address belongs to the applicant.
   */
  readonly featureSkipEmailVerification: boolean;
  /**
   * DEV-ONLY. Keep the funnel session across a page load instead of retiring it.
   *
   * ⚠️ THIS SUSPENDS THE 2026-09-16 DECISION, deliberately and only locally.
   * That decision — every load starts over — is right for a public storefront,
   * but it makes the funnel untestable on a dev server: Vite full-reloads the
   * page on many edits, each reload mints `session?fresh=1`, the previous
   * session is retired, and the `candidate` in the URL no longer resolves. The
   * step then refuses with "Resolve a service address before …", which reads as
   * a product bug and is really just the dev server reloading.
   *
   * When true the gate behaves like an offer arrival: it does NOT retire the
   * session and it DOES read the snapshot (meter only — never the server step,
   * see `FunnelSeedArgs.meterOnly`).
   *
   * Only a literal 'true' enables it, it is never set in `.env.development`
   * (which is committed), and `isDeployed` builds ignore it outright.
   */
  readonly devFunnelResume: boolean;
  /**
   * DEV-ONLY. When true, sensitive POSTs send a placeholder `x-captcha-token` so
   * the funnel is click-throughable before the real Turnstile widget is wired
   * (the CAPTCHA-unconditional `/bff/credit/check` + `/bff/sign/accept` 403 on a
   * MISSING token, before the BFF's dev null-verifier — which passes any token —
   * runs). SAFE: only a literal 'true' (set solely in `.env.development`) enables
   * it, and it FAILS CLOSED in prod — the real Turnstile `siteverify` rejects a
   * stub token, so even if this shipped it could not bypass a real gate. Replace
   * with the real Turnstile widget for any deployed environment.
   */
  readonly devCaptchaStub: boolean;
  /**
   * DEV-ONLY. When true, the deposit-required branch renders a DEMO card form
   * whose "Pay" settles the deposit via the BFF synthetic gateway
   * (`DEPOSIT_GATEWAY_MODE=synthetic`) so the funnel can be walked end-to-end
   * WITHOUT Stripe. No card data is collected — the fields are cosmetic and the
   * BFF takes no money. SAFE: only a literal 'true' enables it, and it FAILS
   * CLOSED in prod — the BFF refuses the synthetic gateway in a deployed config,
   * so a confirm there 501s rather than fabricating a paid deposit.
   */
  readonly devDepositStub: boolean;
  /**
   * ⚠️ INVERTED DEFAULT, DELIBERATELY — this is a §25.478 disclosure gate, and it
   * FAILS SAFE.
   *
   * When true, the deposit figure the funnel shows is an INDIVIDUALLY DETERMINED
   * amount and may be presented as such. Anything else — unset, '', 'false' — means
   * the storefront badges it as an estimate and never lets it read as a final
   * charge (`deposit-disclosure.service.ts`).
   *
   * Every other flag in this file is "off unless the literal string 'true'". This
   * one is the same MECHANISM pointed the other way: over-disclosing an estimate is
   * harmless, whereas presenting a fabricated figure as a real charge is the actual
   * regulatory risk. QA 2026-08-05 found the BFF returning a flat $400 to every
   * customer (it sends Accounts `projectedMonthlyBillMinor: 0`, so Accounts falls
   * back to its cap) — against a statutory one-fifth-of-annual of $385.08 for the
   * meter walked and $203.83 for a 400 kWh/mo home. The BFF-side calculation is
   * being fixed separately; until a deployment can honestly assert the figure is
   * derived from the customer's own projected bill, it must not claim so.
   *
   * Set it to 'true' only once that is true end to end.
   */
  readonly depositAmountVerified: boolean;
  /**
   * DEV-ONLY (SHOP-2 slice 2). When true AND `import.meta.env.DEV` is true, the
   * "Shop by ESI ID" sub-flow and the ZIP-flow address picker read from a
   * hand-rolled TS mock catalogue of 30 Texas ESIIDs (both sub-flows converge on
   * one enriched MeterSummaryCard). Absent/'false' AND in a DEV build → the real
   * BFF path runs (which today fails loud because SHOP-1 hasn't wired
   * `POST /bff/esiid/lookup` yet). SAFE: even if a production build somehow ships
   * with the string 'true', `import.meta.env.DEV` is false in a prod build, and
   * the dev catalogue module has a runtime `assertDevOnly()` throw guard that
   * fires the moment any of its exports is imported outside DEV.
   */
  readonly useMockEsiid: boolean;
  /**
   * ⚠️ THE REP'S PUCT CERTIFICATE NUMBER — A REQUIRED CUSTOMER-FACING DISCLOSURE.
   *
   * The public footer shipped the literal string "PUCT REP License #XXXXX" with
   * the caption "Placeholder — licence number pending configuration". A REP
   * certificate number is a regulatory identifier a customer is entitled to check
   * against the PUCT register; a fabricated one is not a placeholder, it is a
   * false statement about our authorisation to sell them electricity.
   *
   * `undefined` until an operator configures a real number, and the footer then
   * renders NO compliance block at all. Absence is the honest state: a customer
   * who does not see a licence number knows to ask, whereas one who sees #XXXXX
   * has been told something. Never default this to a sample value.
   */
  readonly puctRepLicense: string | undefined;
}

/** Reads an env source and derives the typed, defaulted `ShopEnv`. */
export function readShopEnv(source: ImportMetaEnv): ShopEnv {
  const raw = RawEnvSchema.parse({
    VITE_FEATURE_EFL_PUBLISH: source.VITE_FEATURE_EFL_PUBLISH,
    VITE_FEATURE_SKIP_EMAIL_VERIFICATION: source.VITE_FEATURE_SKIP_EMAIL_VERIFICATION,
    VITE_DEV_FUNNEL_RESUME: source.VITE_DEV_FUNNEL_RESUME,
    VITE_DEV_CAPTCHA_STUB: source.VITE_DEV_CAPTCHA_STUB,
    VITE_DEV_DEPOSIT_STUB: source.VITE_DEV_DEPOSIT_STUB,
    VITE_DEPOSIT_AMOUNT_VERIFIED: source.VITE_DEPOSIT_AMOUNT_VERIFIED,
    VITE_USE_MOCK_ESIID: source.VITE_USE_MOCK_ESIID,
    VITE_PUCT_REP_LICENSE: source.VITE_PUCT_REP_LICENSE,
  });
  // DEV-flag semantics for the mock ESIID catalogue: it must ALSO be a dev build.
  // A missing var in a dev build defaults to ON (per the SHOP-2 slice-2 decision),
  // an explicit 'false' opts back to the real (currently fail-loud) path, and in
  // a prod build the flag is IGNORED (belt-and-braces alongside the module-level
  // `assertDevOnly()` guard in `api/__dev__/`).
  const isDev = source.DEV === true;
  const useMockEsiid =
    isDev && (raw.VITE_USE_MOCK_ESIID === undefined || raw.VITE_USE_MOCK_ESIID !== 'false');
  return {
    featureEflPublish: raw.VITE_FEATURE_EFL_PUBLISH === 'true',
    featureSkipEmailVerification: raw.VITE_FEATURE_SKIP_EMAIL_VERIFICATION === 'true',
    // Dev builds ONLY — a production build ignores the flag outright, so the
    // 2026-09-16 "every load starts over" behaviour cannot be switched off by
    // an env var that leaks into a deployed config.
    devFunnelResume: isDev && raw.VITE_DEV_FUNNEL_RESUME === 'true',
    devCaptchaStub: raw.VITE_DEV_CAPTCHA_STUB === 'true',
    devDepositStub: raw.VITE_DEV_DEPOSIT_STUB === 'true',
    // Same `=== 'true'` mechanism as every flag above; the DIFFERENCE is what the
    // false branch means. See the field doc — this one fails safe by disclosing.
    depositAmountVerified: raw.VITE_DEPOSIT_AMOUNT_VERIFIED === 'true',
    useMockEsiid,
    // Trimmed, and an empty/whitespace value reads as UNSET — a blank env var must
    // not render an empty licence line that looks like a missing value rather than
    // an absent one.
    puctRepLicense:
      raw.VITE_PUCT_REP_LICENSE === undefined || raw.VITE_PUCT_REP_LICENSE.trim() === ''
        ? undefined
        : raw.VITE_PUCT_REP_LICENSE.trim(),
  };
}

/**
 * RUNTIME config injected by the container at startup, NOT baked at build time.
 * The image's entrypoint writes `/env-config.js` from the pod's env (the k8s
 * deployment manifest is the single source of truth — see the infra repo's
 * `Global-Rheora-Frontend-US-Shop/*-manifests/*-deployment.yaml`), and
 * `index.html` loads it before the app bundle. Absent/empty values fall through
 * to the build-time `import.meta.env` (so local `vite dev` still uses
 * `.env.development`).
 */
declare global {
  interface Window {
    __APP_ENV__?: Record<string, string | undefined>;
  }
}

/** Runtime-injected values win over build-time; empty/unset ones fall through. */
function resolveEnvSource(): ImportMetaEnv {
  const runtime =
    (typeof window !== 'undefined' && window.__APP_ENV__) || {};
  const defined: Record<string, string> = {};
  for (const [key, value] of Object.entries(runtime)) {
    if (typeof value === 'string' && value !== '') defined[key] = value;
  }
  return { ...import.meta.env, ...defined } as ImportMetaEnv;
}

export const env: ShopEnv = readShopEnv(resolveEnvSource());
